import React from 'react';
import { Github, Linkedin, Twitter, Link as LinkIcon, Mail, MapPin, Phone, ExternalLink, Code, Briefcase, GraduationCap } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeIn = {
  initial: { opacity: 0, y: 15 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function NewspaperTheme({ profileData, socials, activeColor, trackSocialClick, trackDownload }) {
  const { core, education = [], experience = [], projects = [], skills = [] } = profileData;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-[#f5f0e8] text-[#2a2520] selection:bg-[#2a2520] selection:text-[#f5f0e8]" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
      {/* Newspaper Header */}
      <header className="max-w-5xl mx-auto px-6 pt-10 pb-6 text-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {/* Top rule */}
          <div className="border-t-4 border-b border-double border-[#2a2520] py-1 mb-3">
            <div className="border-t border-[#2a2520]/30" />
          </div>

          {/* Masthead */}
          <h1 className="text-5xl sm:text-7xl font-black tracking-tight leading-none uppercase" style={{ fontFamily: "'Georgia', serif", letterSpacing: '-0.02em' }}>
            {core.name}
          </h1>

          {/* Subtitle line */}
          <div className="flex items-center justify-center gap-4 mt-3 text-[10px] uppercase tracking-[0.3em] text-[#2a2520]/50 font-bold">
            <span>{today}</span>
            <span>•</span>
            <span>Professional Portfolio</span>
            <span>•</span>
            <span>Vol. I, No. 1</span>
          </div>

          {/* Bottom rule */}
          <div className="border-t-2 border-b border-[#2a2520] py-0.5 mt-3">
            <div className="border-t border-[#2a2520]/20" />
          </div>
        </motion.div>
      </header>

      {/* Headline / Summary */}
      {core.summary && (
        <section className="max-w-5xl mx-auto px-6 pb-8">
          <motion.div {...fadeIn} className="text-center border-b border-[#2a2520]/15 pb-8">
            <h2 className="text-2xl sm:text-3xl font-bold leading-snug max-w-3xl mx-auto italic">
              "{core.summary}"
            </h2>
            <div className="flex justify-center gap-4 mt-4">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('github')} className="text-[#2a2520]/40 hover:text-[#2a2520] transition"><Github className="w-5 h-5" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('linkedin')} className="text-[#2a2520]/40 hover:text-[#2a2520] transition"><Linkedin className="w-5 h-5" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('twitter')} className="text-[#2a2520]/40 hover:text-[#2a2520] transition"><Twitter className="w-5 h-5" /></a>}
              {socials.custom && <a href={socials.custom} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('custom')} className="text-[#2a2520]/40 hover:text-[#2a2520] transition"><LinkIcon className="w-5 h-5" /></a>}
            </div>
          </motion.div>
        </section>
      )}

      {/* Multi-column body */}
      <main className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">

          {/* Left column (wide) */}
          <div className="md:col-span-8 space-y-10">
            {/* Experience */}
            {experience.length > 0 && (
              <motion.section {...fadeIn}>
                <h2 className="text-xs uppercase tracking-[0.3em] font-black border-b-2 border-[#2a2520] pb-1 mb-6">
                  Professional Experience
                </h2>
                <div className="space-y-6">
                  {experience.map((exp, i) => (
                    <article key={i} className="border-b border-[#2a2520]/10 pb-6 last:border-0">
                      <h3 className="text-lg font-bold leading-snug">{exp.job_title}</h3>
                      <div className="text-xs text-[#2a2520]/50 font-bold uppercase tracking-wider mt-0.5">
                        {exp.company} — {exp.start_date} to {exp.end_date || 'Present'}
                      </div>
                      <p className="text-sm leading-relaxed mt-2 text-[#2a2520]/70 text-justify" style={{ textIndent: '1.5em' }}>
                        {exp.description}
                      </p>
                    </article>
                  ))}
                </div>
              </motion.section>
            )}

            {/* Projects */}
            {projects.length > 0 && (
              <motion.section {...fadeIn}>
                <h2 className="text-xs uppercase tracking-[0.3em] font-black border-b-2 border-[#2a2520] pb-1 mb-6">
                  Featured Projects
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {projects.map((proj, i) => (
                    <article key={i} className="border-l-2 border-[#2a2520]/20 pl-4">
                      <h3 className="text-base font-bold">{proj.title}</h3>
                      <p className="text-xs leading-relaxed mt-1 text-[#2a2520]/60 text-justify">{proj.description}</p>
                      {proj.link && (
                        <a href={proj.link} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-black text-[#2a2520]/50 hover:text-[#2a2520] mt-2 underline">
                          View Source <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </article>
                  ))}
                </div>
              </motion.section>
            )}
          </div>

          {/* Right column (sidebar) */}
          <div className="md:col-span-4 space-y-8">
            {/* Divider on desktop */}
            <div className="hidden md:block border-l border-[#2a2520]/15 absolute h-full" style={{ left: 'calc(66.67% + 0.5rem)' }} />

            {/* Contact */}
            <motion.div {...fadeIn} className="bg-[#2a2520]/[0.03] p-5 border border-[#2a2520]/10">
              <h3 className="text-[10px] uppercase tracking-[0.3em] font-black border-b border-[#2a2520]/20 pb-1.5 mb-3">
                Contact Information
              </h3>
              <div className="space-y-2 text-xs text-[#2a2520]/60">
                {core.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{core.email}</div>}
                {core.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{core.phone}</div>}
                {core.city && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" />{core.city}, {core.country}</div>}
              </div>
            </motion.div>

            {/* Skills */}
            {skills.length > 0 && (
              <motion.div {...fadeIn}>
                <h3 className="text-[10px] uppercase tracking-[0.3em] font-black border-b border-[#2a2520]/20 pb-1.5 mb-3">
                  Core Competencies
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((sk, i) => (
                    <span key={i} className="px-2.5 py-1 bg-[#2a2520]/5 border border-[#2a2520]/10 text-[10px] font-bold text-[#2a2520]/60">
                      {sk.name || sk}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Education */}
            {education.length > 0 && (
              <motion.div {...fadeIn}>
                <h3 className="text-[10px] uppercase tracking-[0.3em] font-black border-b border-[#2a2520]/20 pb-1.5 mb-3">
                  Education
                </h3>
                <div className="space-y-3">
                  {education.map((edu, i) => (
                    <div key={i} className="text-xs text-[#2a2520]/60">
                      <div className="font-bold text-[#2a2520]/80">{edu.degree}</div>
                      <div>{edu.school || edu.organization_name}</div>
                      <div className="text-[10px] italic">{edu.start_date} – {edu.end_date || 'Present'}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto px-6 pb-10 text-center">
        <div className="border-t-2 border-b border-double border-[#2a2520]/30 py-2">
          <p className="text-[9px] uppercase tracking-[0.4em] text-[#2a2520]/30 font-bold">
            Published with CVLogs Portfolio Builder — {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
}
